import React, { FunctionComponent, useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormOtpBoxes, FormTextField } from '@sbv-clients/ui-forms';
import {
  Button,
  FormHelperText,
  Grid,
  PasswordRules,
  passwordRules,
} from '@sbv-clients/ui-shared';

import {
  authButtonSx,
  authFieldSx,
  authLabelSx,
} from '../../styles/auth-form.styles';
import { BackToSignIn } from '../back-to-sign-in';
import { useValidateUsername } from './utils/use-validate-username';

interface Props {
  validateUsername?: boolean;
  buttonText: string;
  usernameLabel: string;
}

export const CredentialsForm: FunctionComponent<Props> = ({
  validateUsername,
  buttonText,
  usernameLabel,
}) => {
  const {
    formState,
    setError,
    clearErrors,
    getFieldState,
    trigger,
    getValues,
    watch,
  } = useFormContext();
  const password = String(watch('password') ?? '');
  const matchPassword = String(watch('matchPassword') ?? '');
  const { isSubmitting } = formState;
  const checkUsername = useValidateUsername();
  const lastRequestRef = useRef(0);
  const handleValidateUsername = useCallback(
    async (username: string) => {
      try {
        const { data } = await checkUsername.mutateAsync({ username });
        return data;
      } catch (e) {
        /* empty */
      }
    },
    [checkUsername]
  );

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <FormTextField
          density={'comfortable'}
          fullWidth
          name="username"
          type="username"
          label={usernameLabel}
          labelSx={authLabelSx}
          sx={authFieldSx}
          onBlur={async (event) => {
            const value = event?.target?.value || '';
            if (!value) return;
            if (!validateUsername) return;
            const isValid = await trigger('username');
            if (!isValid) return;
            const requestId = ++lastRequestRef.current;
            try {
              const response = await handleValidateUsername(value);
              if (requestId !== lastRequestRef.current) return;
              const currentValue = getValues('username');
              if (currentValue !== value) return;
              if (response?.isUnique === false) {
                setError('username', {
                  type: 'manual',
                  message: 'This username is already taken',
                });
              } else {
                const state = getFieldState('username');
                if (state.error?.type === 'manual') {
                  clearErrors('username');
                }
              }
            } catch {
              setError('username', {
                type: 'manual',
                message:
                  'Could not validate username right now. Please try again later.',
              });
            }
          }}
        />
        {validateUsername && (
          <FormHelperText>
            Please note: this is your SBV username and can’t be adjusted once
            this form is submitted
          </FormHelperText>
        )}
      </Grid>
      <Grid size={12}>
        <FormTextField
          density={'comfortable'}
          fullWidth
          name="password"
          type="password"
          label="New password"
          labelSx={authLabelSx}
          sx={authFieldSx}
        />
        <PasswordRules rules={passwordRules(password)} />
      </Grid>
      <Grid size={12}>
        <FormTextField
          density={'comfortable'}
          fullWidth
          name="matchPassword"
          type="password"
          label="Confirm new password"
          labelSx={authLabelSx}
          sx={authFieldSx}
        />
        <PasswordRules
          rules={[
            {
              label: 'Passwords match',
              ok: !!matchPassword && matchPassword === password,
            },
          ]}
        />
      </Grid>
      <Grid size={12}>
        <FormOtpBoxes labelSx={authLabelSx} />
      </Grid>
      <Grid size={12} pt={10}>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          sx={authButtonSx}
          disabled={isSubmitting}
        >
          {buttonText}
        </Button>
        <BackToSignIn />
      </Grid>
    </Grid>
  );
};
